// Perspective warp of an image into a quadrilateral on a 2D canvas.
// Canvas2D only supports affine transforms, so the quad is split into a grid
// of small triangles — each triangle is mapped exactly, which approximates
// the projective transform closely enough for visual compositing.

export interface Pt { x: number; y: number }

// Solves Ax = b for an n×n system by Gaussian elimination.
const solve = (a: number[][], b: number[]): number[] => {
  const n = b.length;
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) pivot = r;
    }
    [a[col], a[pivot]] = [a[pivot], a[col]];
    [b[col], b[pivot]] = [b[pivot], b[col]];
    const d = a[col][col] || 1e-12;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = a[r][col] / d;
      for (let c = col; c < n; c++) a[r][c] -= f * a[col][c];
      b[r] -= f * b[col];
    }
    for (let c = col; c < n; c++) a[col][c] /= d;
    b[col] /= d;
  }
  return b;
};

// Homography mapping the unit square (0,0)-(1,1) onto the destination quad
// ordered TL, TR, BR, BL.
const homography = (dst: [Pt, Pt, Pt, Pt]): number[] => {
  const a: number[][] = [];
  const b: number[] = [];
  const src: Pt[] = [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const { x: X, y: Y } = dst[i];
    a.push([x, y, 1, 0, 0, 0, -X * x, -X * y]);
    b.push(X);
    a.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]);
    b.push(Y);
  }
  const h = solve(a, b);
  return [...h, 1];
};

const project = (h: number[], x: number, y: number): Pt => {
  const d = h[6] * x + h[7] * y + h[8] || 1e-12;
  return {
    x: (h[0] * x + h[1] * y + h[2]) / d,
    y: (h[3] * x + h[4] * y + h[5]) / d
  };
};

// Draws source triangle (s0,s1,s2 in source px) onto dest triangle exactly.
const drawTriangle = (
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  s0: Pt, s1: Pt, s2: Pt,
  d0: Pt, d1: Pt, d2: Pt
) => {
  const denom = (s1.x - s0.x) * (s2.y - s0.y) - (s2.x - s0.x) * (s1.y - s0.y);
  if (Math.abs(denom) < 1e-8) return;
  const a = ((d1.x - d0.x) * (s2.y - s0.y) - (d2.x - d0.x) * (s1.y - s0.y)) / denom;
  const b = ((d1.y - d0.y) * (s2.y - s0.y) - (d2.y - d0.y) * (s1.y - s0.y)) / denom;
  const c = ((d2.x - d0.x) * (s1.x - s0.x) - (d1.x - d0.x) * (s2.x - s0.x)) / denom;
  const d = ((d2.y - d0.y) * (s1.x - s0.x) - (d1.y - d0.y) * (s2.x - s0.x)) / denom;
  const e = d0.x - a * s0.x - c * s0.y;
  const f = d0.y - b * s0.x - d * s0.y;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(d0.x, d0.y);
  ctx.lineTo(d1.x, d1.y);
  ctx.lineTo(d2.x, d2.y);
  ctx.closePath();
  // Slight inflation hides hairline seams between adjacent cells.
  const cx = (d0.x + d1.x + d2.x) / 3;
  const cy = (d0.y + d1.y + d2.y) / 3;
  ctx.transform(1.005, 0, 0, 1.005, cx - 1.005 * cx, cy - 1.005 * cy);
  ctx.clip();
  ctx.transform(a, b, c, d, e, f);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
};

// Warps `img` (a transparent door render) into the quad on `ctx`.
// `quad` corners are in canvas pixel coordinates, ordered TL, TR, BR, BL.
export const warpImageIntoQuad = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  quad: [Pt, Pt, Pt, Pt],
  subdivisions = 32
) => {
  const h = homography(quad);
  const w = img.naturalWidth;
  const hh = img.naturalHeight;
  const n = Math.max(1, subdivisions);
  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const u0 = col / n;
      const u1 = (col + 1) / n;
      const v0 = row / n;
      const v1 = (row + 1) / n;
      const sTL = { x: u0 * w, y: v0 * hh };
      const sTR = { x: u1 * w, y: v0 * hh };
      const sBR = { x: u1 * w, y: v1 * hh };
      const sBL = { x: u0 * w, y: v1 * hh };
      const dTL = project(h, u0, v0);
      const dTR = project(h, u1, v0);
      const dBR = project(h, u1, v1);
      const dBL = project(h, u0, v1);
      drawTriangle(ctx, img, sTL, sTR, sBR, dTL, dTR, dBR);
      drawTriangle(ctx, img, sTL, sBR, sBL, dTL, dBR, dBL);
    }
  }
};
